/**
 * Settings — same structure and words as the phone app (apps/mobile settings_screen.dart):
 * a profile card, then "Invite a friend", "Privacy & security", "Notifications", "Chats",
 * "Network & delivery", "Storage & data", "Help & about", and "Lock now".
 * Wide screens: the list is a left column and the chosen page sits beside it. Narrow phones:
 * the list is the first page and every section opens as its own page with a Back button.
 * Web-only settings (backup, linked devices, security log, lock when hidden, …) live in the
 * section they belong to.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  DISAPPEAR_OPTIONS,
  unblockContact,
  type Account,
  type Contact,
  type Conversation,
  type DisappearTimerSec,
  type SecurityEvent,
} from "@veto/domain";
import { sessionSuiteV2, type WasmIdentity } from "@veto/crypto-bridge";
import type { RelayPool } from "@veto/transport";
import {
  Avatar,
  IconBell,
  IconBlock,
  IconChat,
  IconData,
  IconDevice,
  IconDoc,
  IconEdit,
  IconEye,
  IconGif,
  IconGlobe,
  IconImage,
  IconInfo,
  IconLock,
  IconPalette,
  IconQr,
  IconSend,
  IconShield,
  IconStar,
  IconTimer,
  IconTrash,
} from "@veto/ui";
import { acknowledgeSecurityEvent, getMeta, saveContact } from "@veto/storage";
import type { NotificationPermissionState, NotificationPrefs } from "../notifications";
import type { SecureVaultCapabilities } from "../secureVault";
import { LinkedDevicesPanel } from "../components/LinkedDevicesPanel";
import { BackupExportButton } from "../components/BackupExportButton";
import { BackupImportButton } from "../components/BackupImportButton";
import { SecureWipeButton } from "../components/SecureWipeButton";
import { ModalShell } from "../components/ModalShell";
import { ContactVerify } from "../components/ContactVerify";
import { confirmAction } from "../components/ConfirmDialog";
import { loadMediaPrefs } from "../mediaPrefs";
import { loadGifPrefs, saveGifPrefs, type GifPrefs } from "../gifPrefs";
import {
  applyAppearancePrefs,
  loadAppearancePrefs,
  loadEnterSends,
  saveAppearancePrefs,
  saveEnterSends,
  type AppearancePrefs,
  type TextSize,
} from "../appearancePrefs";
import {
  AUTO_LOCK_CHOICES,
  autoLockLabel,
  loadLockPrefs,
  saveLockPrefs,
  type LockPrefs,
} from "../lockPrefs";
import { defaultDisappearSummary, loadDefaultDisappear, saveDefaultDisappear } from "../chatPrefs";
import { getThemePref, setTheme, THEME_EVENT, type ThemePref } from "../theme";
import { useProfileMediaUrl } from "../profile/profileMedia";
import { NetworkPanel } from "./NetworkPanel";
import { ChangePasscodeModal } from "./settings/ChangePasscodeModal";
import {
  BlockRow,
  ChoiceSheet,
  Group,
  IconBadge,
  InfoRow,
  NavRow,
  PageHead,
  Segmented,
  SwitchRow,
  TINT,
} from "./settings/SettingsRows";

export type SettingsSection =
  "privacy" | "notifications" | "chats" | "network" | "storage" | "about";
/** Sections plus the pages opened from "Privacy & security". */
export type SettingsTab = SettingsSection | "blocked" | "verify" | "devices" | "events";

export const SETTINGS_SECTIONS: ReadonlyArray<{ id: SettingsSection; label: string }> = [
  { id: "privacy", label: "Privacy & security" },
  { id: "notifications", label: "Notifications" },
  { id: "chats", label: "Chats" },
  { id: "network", label: "Network & delivery" },
  { id: "storage", label: "Storage & data" },
  { id: "about", label: "Help & about" },
];

const SUB_PAGES: Record<"blocked" | "verify" | "devices" | "events", string> = {
  blocked: "Blocked contacts",
  verify: "Verify contacts",
  devices: "Linked devices",
  events: "Security events",
};

/** Older tab ids (before the phone-style regrouping) still land somewhere sensible. */
export function normalizeSettingsTab(tab: string): SettingsTab {
  switch (tab) {
    case "security":
      return "privacy";
    case "appearance":
      return "chats";
    case "data":
      return "storage";
    case "privacy":
    case "notifications":
    case "chats":
    case "network":
    case "storage":
    case "about":
    case "blocked":
    case "verify":
    case "devices":
    case "events":
      return tab;
    default:
      return "privacy";
  }
}

export function sectionOf(tab: SettingsTab): SettingsSection {
  return tab in SUB_PAGES ? "privacy" : (tab as SettingsSection);
}

export const SUPPORT_URL = "https://martialtech.fr/veto/support/";
export const PRIVACY_URL = "https://martialtech.fr/veto/privacy/";
export const TERMS_URL = "https://martialtech.fr/veto/terms/";
const SUPPORT_EMAIL = "support@martialtech.fr";

const APP_VERSION: string = (import.meta.env?.VITE_VETO_VERSION as string | undefined) ?? "";

/** The exact session suite, straight from the Rust core (bound into every handshake). */
function sessionSuite(): string | null {
  try {
    return sessionSuiteV2();
  } catch {
    return null; // WASM not loaded (tests / before unlock)
  }
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 MB";
  const mb = n / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb >= 10) return `${Math.round(mb)} MB`;
  return `${mb.toFixed(1)} MB`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Contacts the phone lists under "Verify contacts": not blocked, not a pending request. */
export function contactsToVerify(contacts: Contact[]): Contact[] {
  return contacts
    .filter(
      (c) => !c.blockedAt && c.requestState !== "pending" && c.verificationState !== "verified",
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

type StorageEstimate = { usage: number; quota: number; db?: number; app?: number };

// Stable defaults: a fresh [] per render would re-run the effects that depend on them.
const NO_CONTACTS: Contact[] = [];
const NO_CONVERSATIONS: Conversation[] = [];

export function SettingsPanel({
  account,
  tab,
  onTab,
  onClose,
  contacts = NO_CONTACTS,
  conversations = NO_CONVERSATIONS,
  openSecurityCount,
  securityEvents,
  relayStatus,
  relayClient,
  vaultCaps,
  notificationPrefs,
  notificationPermission,
  onNotificationPrefsChange,
  onRequestNotificationPermission,
  onLockNow,
  onToast,
  sessionIdentity,
  onOpenProfile,
  onInvite,
  onRefresh,
  onVerifyContact,
  mediaCacheCount = 0,
  onClearMediaCache,
  onShowStarred,
}: {
  account: Account;
  tab: SettingsTab;
  onTab: (t: SettingsTab) => void;
  onClose: () => void;
  contacts?: Contact[];
  conversations?: Conversation[];
  openSecurityCount: number;
  securityEvents: SecurityEvent[];
  relayStatus: string;
  relayClient?: RelayPool | null;
  vaultCaps: SecureVaultCapabilities | null;
  notificationPrefs: NotificationPrefs;
  notificationPermission: NotificationPermissionState;
  onNotificationPrefsChange: (prefs: NotificationPrefs) => void;
  onRequestNotificationPermission: () => Promise<NotificationPermissionState> | void;
  onLockNow?: () => void;
  onToast: (msg: string) => void;
  /** Live WASM identity for Ed25519 device-approval signatures (FR-DEV-001). Raw keys never leave WASM. */
  sessionIdentity: WasmIdentity | null;
  /** Opens the profile editor (photo, name, about). */
  onOpenProfile?: () => void;
  /** Opens "Invite a friend" (your link / QR). */
  onInvite?: () => void;
  /** Reload contacts / conversations / security events after a change made here. */
  onRefresh?: () => Promise<void> | void;
  /** Mark a contact verified after the out-of-band comparison (FR-CON-002). */
  onVerifyContact?: (contact: Contact) => Promise<void> | void;
  /** Decrypted photos, files and voice notes held in memory right now. */
  mediaCacheCount?: number;
  /** Drop those decrypted copies; returns how many were removed. */
  onClearMediaCache?: () => number;
  /** Opens the starred messages of every chat. */
  onShowStarred?: () => void;
}) {
  const current = normalizeSettingsTab(tab);
  const section = sectionOf(current);
  // Narrow phones: the section list is a page of its own ("home"); wide screens show both.
  const [view, setView] = useState<"home" | "page">("home");
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [lockPrefs, setLockPrefs] = useState<LockPrefs>(() => loadLockPrefs());
  const [gifPrefs, setGifPrefs] = useState<GifPrefs>(() => loadGifPrefs());
  const [appearance, setAppearance] = useState<AppearancePrefs>(() => loadAppearancePrefs());
  const [enterSends, setEnterSends] = useState(() => loadEnterSends());
  const [defaultDisappear, setDefaultDisappear] = useState<DisappearTimerSec>(() =>
    loadDefaultDisappear(),
  );
  const [themePref, setThemePrefState] = useState<ThemePref>(() => getThemePref());
  const [storage, setStorage] = useState<StorageEstimate | null>(null);
  const [lockedChats, setLockedChats] = useState<number | null>(null);
  const [chooser, setChooser] = useState<"autolock" | "disappear" | null>(null);
  const [changingPasscode, setChangingPasscode] = useState(false);
  const [verifyId, setVerifyId] = useState<string | null>(null);
  // Keyboard / screen-reader users follow the navigation: a new page gets focus on its title,
  // Back to the list returns focus to the list.
  const titleId = useId();
  const focusTarget = useRef<"page" | "home" | null>(null);

  // Stable close for the shell (parents pass a fresh function on every render).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const close = useCallback(() => closeRef.current(), []);
  const closeChooser = useCallback(() => setChooser(null), []);
  const closePasscode = useCallback(() => setChangingPasscode(false), []);

  // Theme may change from the top-bar toggle or the OS ("System") — keep the Chats page in sync.
  useEffect(() => {
    const onTheme = () => setThemePrefState(getThemePref());
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  // Local storage usage (SPEC §5.5 Data — quota & retention visibility).
  useEffect(() => {
    if (section !== "storage") return;
    let alive = true;
    navigator.storage
      ?.estimate?.()
      .then((e) => {
        if (!alive) return;
        const details = (e as StorageEstimate & { usageDetails?: Record<string, number> })
          .usageDetails;
        setStorage({
          usage: e.usage ?? 0,
          quota: e.quota ?? 0,
          db: details?.indexedDB,
          app:
            details && (details.caches != null || details.serviceWorkerRegistrations != null)
              ? (details.caches ?? 0) + (details.serviceWorkerRegistrations ?? 0)
              : undefined,
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [section]);

  // How many chats carry a chat lock (each opens only with its own PIN).
  useEffect(() => {
    if (section !== "privacy") return;
    let alive = true;
    void Promise.all(conversations.map(async (c) => ((await getMeta(`chatlock.${c.id}`)) ? 1 : 0)))
      .then((flags) => {
        if (alive) setLockedChats(flags.reduce<number>((a, b) => a + b, 0));
      })
      .catch(() => {
        if (alive) setLockedChats(null);
      });
    return () => {
      alive = false;
    };
  }, [section, conversations]);

  // A new page starts at its top.
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [current, verifyId]);

  useEffect(() => {
    const target = focusTarget.current;
    focusTarget.current = null;
    if (!target) return;
    if (target === "home") {
      document.querySelector<HTMLElement>('.sv-index [aria-current="page"]')?.focus();
      return;
    }
    // Wide screens keep focus in the list for top-level sections (both stay visible).
    const narrow = window.matchMedia?.("(max-width: 767px)").matches ?? false;
    if (narrow || current in SUB_PAGES || verifyId) document.getElementById(titleId)?.focus();
  });

  function openTab(t: SettingsTab) {
    focusTarget.current = "page";
    setVerifyId(null);
    onTab(t);
    setView("page");
  }

  function openVerify(id: string) {
    focusTarget.current = "page";
    setVerifyId(id);
  }

  function updateLockPrefs(next: LockPrefs) {
    setLockPrefs(next);
    saveLockPrefs(next);
  }

  function updateAppearance(next: AppearancePrefs) {
    setAppearance(next);
    saveAppearancePrefs(next);
    applyAppearancePrefs(next);
  }

  function updateNotifications(next: NotificationPrefs) {
    onNotificationPrefsChange(next);
  }

  async function setNotificationsOn(on: boolean) {
    updateNotifications({ ...notificationPrefs, enabled: on });
    if (on && notificationPermission === "default") await onRequestNotificationPermission();
  }

  const toVerify = useMemo(() => contactsToVerify(contacts), [contacts]);
  const blocked = useMemo(
    () =>
      contacts
        .filter((c) => !!c.blockedAt)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [contacts],
  );
  const readyRelays = relayClient?.statuses?.().filter((s) => s.connected).length ?? 0;
  const avatarUrl = useProfileMediaUrl(account.avatar ?? null);
  const suite = useMemo(() => sessionSuite(), []);

  /* ----------------------------------------------------------- summaries -- */

  const privacySummary = [
    lockPrefs.autoLockMinutes > 0
      ? `Auto-lock: ${autoLockLabel(lockPrefs.autoLockMinutes).toLowerCase()}`
      : "Auto-lock off",
    toVerify.length > 0 ? `${toVerify.length} to verify` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const notificationsSummary = !notificationPrefs.enabled
    ? "Off"
    : notificationPermission === "denied"
      ? "On · blocked in browser settings"
      : notificationPermission === "default"
        ? "On · waiting for your browser's permission"
        : notificationPrefs.showPreviews
          ? "On · name and message"
          : "On · content hidden";
  const networkSummary =
    readyRelays >= 2
      ? `Healthy · ${readyRelays} relays`
      : readyRelays === 1
        ? "Limited · 1 relay"
        : relayStatus === "connected"
          ? "Connected"
          : "Offline · messages queued";

  /* --------------------------------------------------------------- pages -- */

  const sectionLabel = SETTINGS_SECTIONS.find((s) => s.id === section)?.label ?? "Settings";
  const pageTitle =
    current in SUB_PAGES ? SUB_PAGES[current as keyof typeof SUB_PAGES] : sectionLabel;
  const isSubPage = current in SUB_PAGES;

  function back() {
    if (verifyId) {
      focusTarget.current = "page";
      setVerifyId(null);
      return;
    }
    if (isSubPage) {
      focusTarget.current = "page";
      onTab("privacy");
      return;
    }
    focusTarget.current = "home";
    setView("home");
  }

  const privacyPage = (
    <>
      <Group title="App lock">
        <NavRow
          id="set-autolock"
          icon={<IconTimer size={18} />}
          color={TINT.green}
          title="Auto-lock"
          subtitle={autoLockLabel(lockPrefs.autoLockMinutes)}
          onClick={() => setChooser("autolock")}
        />
        <SwitchRow
          id="set-lock-on-hide"
          icon={<IconEye size={18} />}
          color={TINT.green}
          title="Lock when Veto is hidden"
          subtitle="The auto-lock timer starts as soon as you switch to another tab or app."
          checked={lockPrefs.lockOnHide}
          onChange={(v) => updateLockPrefs({ ...lockPrefs, lockOnHide: v })}
        />
        <NavRow
          id="set-change-passcode"
          icon={<IconLock size={18} />}
          color={TINT.green}
          title="Change passcode"
          subtitle="Your messages stay as they are; only the key that protects them is re-wrapped"
          onClick={() => setChangingPasscode(true)}
        />
      </Group>

      <Group title="Privacy">
        <InfoRow
          icon={<IconLock size={18} />}
          color={TINT.blue}
          title="Locked chats"
          subtitle={
            lockedChats
              ? `${lockedChats} locked · in your chat list under Locked chats, each opens with its own PIN`
              : "Right-click or long-press a chat → Lock chat"
          }
        />
        <NavRow
          id="set-blocked"
          icon={<IconBlock size={18} />}
          color={TINT.coral}
          title="Blocked contacts"
          subtitle={blocked.length === 0 ? "Nobody" : plural(blocked.length, "contact", "contacts")}
          onClick={() => openTab("blocked")}
        />
        <NavRow
          id="set-verify"
          icon={<IconShield size={18} />}
          color={TINT.green}
          title="Verify contacts"
          subtitle={
            toVerify.length === 0
              ? "Everyone you chat with is verified"
              : `${toVerify.length} not verified yet — compare safety numbers`
          }
          onClick={() => openTab("verify")}
        />
        <InfoRow
          icon={<IconEye size={18} />}
          color={TINT.slate}
          title="Read receipts & typing"
          subtitle="Veto doesn't tell your contacts when you read their messages or while you're typing. Delivery ticks only mean the relays have your message."
        />
        <SwitchRow
          id="set-gif-search"
          icon={<IconGif size={18} />}
          color={TINT.purple}
          title="Online GIF search"
          subtitle="Off by default. Searches Tenor through Veto's own proxy; importing your own GIFs and stickers always works without it."
          checked={gifPrefs.onlineSearchEnabled}
          onChange={(v) => {
            const next = { onlineSearchEnabled: v };
            setGifPrefs(next);
            saveGifPrefs(next);
          }}
        />
        <InfoRow
          icon={<IconEye size={18} />}
          color={TINT.slate}
          title="Screen security"
          subtitle="A web app can't block screenshots or screen recording, and your browser may show a preview of this tab. Lock Veto before you share your screen — the Android app blocks screenshots."
        />
      </Group>

      <Group title="Devices & alerts">
        <NavRow
          id="set-devices"
          icon={<IconDevice size={18} />}
          color={TINT.blue}
          title="Linked devices"
          subtitle="Approve or revoke other devices"
          onClick={() => openTab("devices")}
        />
        <NavRow
          id="set-events"
          icon={<IconShield size={18} />}
          color={TINT.amber}
          title="Security events"
          subtitle={
            openSecurityCount > 0
              ? `${plural(openSecurityCount, "new alert", "new alerts")} — key changes, devices, requests`
              : securityEvents.length > 0
                ? "No new alerts"
                : "No key-change or device alerts yet"
          }
          badge={openSecurityCount}
          onClick={() => openTab("events")}
        />
        <InfoRow
          icon={<IconDevice size={18} />}
          color={TINT.slate}
          title="Secure storage"
          subtitle={
            vaultCaps
              ? vaultCaps.hardwareBacked
                ? `${vaultCaps.backend} · hardware-backed`
                : "Your vault is encrypted with your passcode. This browser has no hardware-backed key store."
              : "…"
          }
        />
      </Group>

      <Group title="Encryption">
        <InfoRow
          icon={<IconLock size={18} />}
          color={TINT.purple}
          title="End-to-end encryption"
          subtitle="Protocol v2: authenticated X25519 + ML-KEM-768 handshake, Double Ratchet with periodic ML-KEM re-keys, sealed envelopes. Keys are created on this device and never leave it."
        />
        {suite && (
          <InfoRow
            icon={<IconShield size={18} />}
            color={TINT.purple}
            title="Suite"
            subtitle={<code className="sv-suite">{suite}</code>}
          />
        )}
        <InfoRow
          icon={<IconInfo size={18} />}
          color={TINT.slate}
          title="Honest status"
          subtitle="Post-quantum protection covers message confidentiality; identity checks are classical. Verify safety numbers for maximum assurance."
        />
      </Group>
    </>
  );

  const blockedPage = (
    <>
      {blocked.length === 0 ? (
        <p className="sv-empty">Nobody is blocked</p>
      ) : (
        <Group>
          {blocked.map((c) => (
            <PersonRow
              key={c.id}
              contact={c}
              action={
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    void (async () => {
                      await saveContact(unblockContact(c));
                      await onRefresh?.();
                      onToast(`${c.displayName} unblocked`);
                    })();
                  }}
                  aria-label={`Unblock ${c.displayName}`}
                >
                  Unblock
                </button>
              }
            />
          ))}
        </Group>
      )}
    </>
  );

  const verifying = verifyId ? (contacts.find((c) => c.id === verifyId) ?? null) : null;
  const verifyPage = verifying ? (
    <div className="stack">
      <PersonRow contact={verifying} subtitle={verifyStateLabel(verifying)} />
      <ContactVerify
        contact={verifying}
        onVerify={async () => {
          await onVerifyContact?.(verifying);
          setVerifyId(null);
        }}
      />
    </div>
  ) : (
    <>
      <p className="muted text-sm sv-lead">
        Compare safety numbers in person or on a call you trust. It proves nobody sits between you.
      </p>
      {toVerify.length === 0 ? (
        <p className="sv-empty">Everyone you chat with is verified</p>
      ) : (
        <Group>
          {toVerify.map((c) => (
            <PersonRow
              key={c.id}
              contact={c}
              subtitle={verifyStateLabel(c)}
              warn={c.verificationState === "changed"}
              onClick={() => openVerify(c.id)}
            />
          ))}
        </Group>
      )}
    </>
  );

  const eventsPage = (
    <>
      {securityEvents.length === 0 && (
        <p className="sv-empty">No key-change or device alerts yet</p>
      )}
      {openSecurityCount > 0 && (
        <button
          type="button"
          className="btn small secondary sv-inline-action"
          onClick={() => {
            void (async () => {
              for (const ev of securityEvents) {
                if (!ev.acknowledged) await acknowledgeSecurityEvent(ev.id);
              }
              await onRefresh?.();
            })();
          }}
        >
          Mark all as seen
        </button>
      )}
      <div className="stack">
        {securityEvents.slice(0, 30).map((ev) => (
          <div
            key={ev.id}
            className={`device-card ${ev.kind === "key_change" ? "security-alert" : ""}`}
            role="status"
          >
            <strong>
              {eventLabel(ev.kind)} {!ev.acknowledged ? "· new" : "· seen"}
            </strong>
            <div className="event-meta">
              {new Date(ev.createdAt).toLocaleString()} — {ev.detail}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const devicesPage = (
    <div className="sv-embedded">
      <LinkedDevicesPanel
        accountFingerprint={account.messagingPublicKeyHex}
        assuranceMode={account.assuranceMode}
        onToast={onToast}
        signWithIdentity={sessionIdentity ? (m: Uint8Array) => sessionIdentity.sign(m) : null}
      />
    </div>
  );

  const notificationsPage = (
    <>
      {notificationPermission === "denied" && notificationPrefs.enabled && (
        <Group>
          <InfoRow
            icon={<IconBell size={18} />}
            color={TINT.amber}
            title="Blocked in your browser settings"
            subtitle="Allow notifications for this site in your browser's settings to receive them."
          />
        </Group>
      )}
      {notificationPermission === "default" && notificationPrefs.enabled && (
        <Group>
          <NavRow
            id="set-notif-allow"
            icon={<IconBell size={18} />}
            color={TINT.amber}
            title="Allow notifications in this browser"
            subtitle="Your browser asks once. Veto works without them."
            onClick={() => void onRequestNotificationPermission()}
          />
        </Group>
      )}
      {notificationPermission === "unsupported" && (
        <Group>
          <InfoRow
            icon={<IconBell size={18} />}
            color={TINT.amber}
            title="Not available here"
            subtitle="This browser can't show notifications. In-app banners still work while Veto is open."
          />
        </Group>
      )}
      <Group
        title="Messages"
        footer="Notifications are made by Veto in this browser — no push service sees them, so they only arrive while Veto is open and unlocked (in a tab or as an installed app). Muted chats stay silent and locked chats never show who or what."
      >
        <SwitchRow
          id="set-notif-enabled"
          icon={<IconBell size={18} />}
          color={TINT.coral}
          title="Message notifications"
          subtitle="System notifications for chats you don't have open"
          checked={notificationPrefs.enabled}
          onChange={(v) => void setNotificationsOn(v)}
          disabled={notificationPermission === "unsupported"}
        />
        <SwitchRow
          id="set-notif-previews"
          icon={<IconEye size={18} />}
          color={TINT.coral}
          title="Show name and message"
          subtitle={
            "Off: notifications only say \"New encrypted message\". On: the sender's name and the start of the message appear in your system's notifications."
          }
          checked={notificationPrefs.showPreviews}
          onChange={(v) => updateNotifications({ ...notificationPrefs, showPreviews: v })}
          disabled={!notificationPrefs.enabled}
        />
        <SwitchRow
          id="set-notif-sound"
          icon={<IconBell size={18} />}
          color={TINT.coral}
          title="Sound"
          subtitle="Notifications and banners make a sound. Played on this device only."
          checked={notificationPrefs.sound}
          onChange={(v) => updateNotifications({ ...notificationPrefs, sound: v })}
        />
      </Group>
      <Group title="While Veto is open">
        <SwitchRow
          id="set-notif-banners"
          icon={<IconChat size={18} />}
          color={TINT.blue}
          title="In-app banners"
          subtitle="A banner at the top for messages in other chats"
          checked={notificationPrefs.inAppBanners !== false}
          onChange={(v) => updateNotifications({ ...notificationPrefs, inAppBanners: v })}
        />
        <SwitchRow
          id="set-notif-focused"
          icon={<IconBell size={18} />}
          color={TINT.blue}
          title="System notifications too"
          subtitle="Also show system notifications while Veto is the tab you're looking at."
          checked={notificationPrefs.notifyWhileFocused}
          onChange={(v) => updateNotifications({ ...notificationPrefs, notifyWhileFocused: v })}
          disabled={!notificationPrefs.enabled}
        />
      </Group>
    </>
  );

  const chatsPage = (
    <>
      <Group title="Display">
        <BlockRow icon={<IconPalette size={18} />} color={TINT.blue} title="Theme">
          <Segmented<ThemePref>
            label="Theme"
            name="veto-theme"
            value={themePref}
            options={[
              ["dark", "Dark"],
              ["light", "Light"],
              ["system", "System"],
            ]}
            onChange={(v) => {
              setThemePrefState(v);
              setTheme(v);
            }}
          />
        </BlockRow>
        <BlockRow
          icon={<IconEdit size={18} />}
          color={TINT.blue}
          title="Text size"
          subtitle="Scales all text on this device"
        >
          <Segmented<TextSize>
            label="Text size"
            name="veto-text-size"
            value={appearance.textSize}
            options={[
              ["sm", "Small"],
              ["md", "Default"],
              ["lg", "Large"],
            ]}
            onChange={(v) => updateAppearance({ ...appearance, textSize: v })}
          />
        </BlockRow>
        <SwitchRow
          id="set-reduced-motion"
          icon={<IconEye size={18} />}
          color={TINT.blue}
          title="Reduce motion"
          subtitle="Fewer animations"
          checked={appearance.reducedMotion}
          onChange={(v) => updateAppearance({ ...appearance, reducedMotion: v })}
        />
      </Group>
      <Group title="Writing">
        <SwitchRow
          id="set-enter-sends"
          icon={<IconSend size={18} />}
          color={TINT.teal}
          title="Enter key sends"
          subtitle={
            enterSends ? "On: Shift+Enter starts a new line" : "Off: Enter starts a new line"
          }
          checked={enterSends}
          onChange={(v) => {
            setEnterSends(v);
            saveEnterSends(v);
          }}
        />
        <NavRow
          id="set-default-disappear"
          icon={<IconTimer size={18} />}
          color={TINT.teal}
          title="Disappearing messages for new chats"
          subtitle={defaultDisappearSummary(defaultDisappear)}
          onClick={() => setChooser("disappear")}
        />
      </Group>
      <Group
        title="Your chats"
        footer="Disappearing messages, chat locks, pins, mutes and archive are set per chat — right-click or long-press a chat, or open its details. Recipients may keep copies of disappearing messages."
      >
        {onShowStarred && (
          <NavRow
            id="set-starred"
            icon={<IconStar size={18} />}
            color={TINT.amber}
            title="Starred messages"
            subtitle="From all your chats"
            onClick={onShowStarred}
          />
        )}
        <InfoRow
          icon={<IconChat size={18} />}
          color={TINT.slate}
          title="Per-chat settings"
          subtitle="Each chat keeps its own"
        />
      </Group>
    </>
  );

  const networkPage = (
    <div className="sv-embedded">
      <NetworkPanel relayStatus={relayStatus} relayClient={relayClient} onToast={onToast} />
    </div>
  );

  const mediaPrefs = loadMediaPrefs();
  const storagePage = (
    <>
      <Group
        title="On this device"
        footer="Messages, contacts and keys are stored encrypted in this browser. Photos, files and voice notes are decrypted into memory only while Veto is unlocked; locking clears them."
      >
        {storage?.db != null ? (
          <>
            <InfoRow
              icon={<IconChat size={18} />}
              color={TINT.purple}
              title="Messages & contacts"
              subtitle="Encrypted database"
              value={formatBytes(storage.db)}
            />
            {storage.app != null && (
              <InfoRow
                icon={<IconData size={18} />}
                color={TINT.purple}
                title="App files for offline use"
                value={formatBytes(storage.app)}
              />
            )}
          </>
        ) : (
          <InfoRow
            icon={<IconData size={18} />}
            color={TINT.purple}
            title="Space used"
            subtitle="Messages, contacts, keys and the app's offline files"
            value={storage ? formatBytes(storage.usage) : "…"}
          />
        )}
        <InfoRow
          icon={<IconImage size={18} />}
          color={TINT.purple}
          title="Media cache"
          subtitle="Decrypted this session, in memory"
          value={plural(mediaCacheCount, "file", "files")}
        />
        <NavRow
          id="set-clear-media"
          icon={<IconTrash size={18} />}
          color={TINT.slate}
          title="Clear media cache"
          subtitle="Remove decrypted photos, files and voice notes from this device"
          chevron={false}
          disabled={!onClearMediaCache}
          onClick={() => {
            void (async () => {
              const ok = await confirmAction({
                title: "Clear media cache?",
                body: "Photos, files and voice notes you received are removed from this device. Veto for the web keeps them only in memory while it is unlocked, so they can't be opened again afterwards.",
                action: "Clear",
              });
              if (!ok || !onClearMediaCache) return;
              const n = onClearMediaCache();
              onToast(
                n === 0
                  ? "Nothing to clear"
                  : n === 1
                    ? "Cleared 1 attachment"
                    : `Cleared ${n} attachments`,
              );
            })();
          }}
        />
        <SwitchRow
          id="set-wifi-only"
          icon={<IconImage size={18} />}
          color={TINT.slate}
          title="Download on Wi-Fi only"
          subtitle="Not available in the web app yet — attachments you receive download while Veto is open."
          checked={mediaPrefs.wifiOnlyAutoDownload}
          onChange={() => {}}
          disabled
        />
      </Group>
      <Group
        title="Backup"
        footer="There is no account on a server and no operator reset — keep your encrypted backup safe."
      >
        <div className="sv-row sv-row-form">
          <BackupExportButton account={account} onToast={onToast} />
        </div>
        <div className="sv-row sv-row-form">
          <BackupImportButton onToast={onToast} />
        </div>
      </Group>
      <section className="sv-group" aria-label="Delete everything">
        <SecureWipeButton onToast={onToast} />
        <p className="sv-group-footer">
          Uninstalling Veto or clearing this site&apos;s data in your browser also removes
          everything.
        </p>
      </section>
    </>
  );

  const aboutPage = (
    <>
      <div className="sv-about-brand">
        <img className="sv-about-mark" src="/icons/icon.svg" width={64} height={64} alt="" />
        <strong className="sv-about-name">Veto</strong>
        <span className="muted">Unscanned. Uncontrolled. End-to-end.</span>
        {APP_VERSION && <span className="muted text-xs">Version {APP_VERSION}</span>}
      </div>
      <Group>
        <LinkRow
          href={SUPPORT_URL}
          icon={<IconChat size={18} />}
          color={TINT.blue}
          title="Support"
          subtitle={SUPPORT_EMAIL}
        />
        <LinkRow
          href={PRIVACY_URL}
          icon={<IconShield size={18} />}
          color={TINT.green}
          title="Privacy policy"
        />
        <LinkRow
          href={TERMS_URL}
          icon={<IconDoc size={18} />}
          color={TINT.slate}
          title="Terms of use"
        />
      </Group>
      <Group title="What Veto is">
        <InfoRow
          icon={<IconInfo size={18} />}
          color={TINT.slate}
          title="Messages that stay between people"
          subtitle="Not scanners, not relays, not operators. No phone number. Hybrid post-quantum encryption (X25519 + ML-KEM-768) and private multi-relay delivery — relays only ever carry sealed envelopes."
        />
        <InfoRow
          icon={<IconShield size={18} />}
          color={TINT.slate}
          title="Honest by design"
          subtitle="No messenger can promise perfect security. Verify your contacts and keep your backup safe."
        />
      </Group>
    </>
  );

  const pages: Record<SettingsTab, React.ReactNode> = {
    privacy: privacyPage,
    blocked: blockedPage,
    verify: verifyPage,
    devices: devicesPage,
    events: eventsPage,
    notifications: notificationsPage,
    chats: chatsPage,
    network: networkPage,
    storage: storagePage,
    about: aboutPage,
  };

  /* ---------------------------------------------------------------- shell -- */

  return (
    <ModalShell
      title="Settings"
      ariaLabel="Settings"
      onClose={close}
      size="lg"
      className="settings-card"
    >
      <div className="settings-v3" data-view={view}>
        <nav className="sv-index" aria-label="Settings sections">
          <button
            type="button"
            className="sv-profile"
            onClick={onOpenProfile}
            id="set-open-profile"
          >
            <Avatar
              name={account.displayName}
              size={56}
              media={
                avatarUrl && account.avatar ? { url: avatarUrl, kind: account.avatar.kind } : null
              }
            />
            <span className="sv-profile-text">
              <strong>{account.displayName}</strong>
              <span className="muted">
                {account.about?.trim() || "Add a photo and an about line"}
              </span>
            </span>
            <span className="sv-chevron" aria-hidden>
              ›
            </span>
          </button>

          <Group>
            <NavRow
              id="set-invite"
              icon={<IconQr size={18} />}
              color={TINT.blue}
              title="Invite a friend"
              subtitle="Your link or QR code — no phone number"
              onClick={onInvite}
            />
          </Group>
          <Group>
            <NavRow
              icon={<IconShield size={18} />}
              color={TINT.green}
              title="Privacy & security"
              subtitle={privacySummary}
              badge={openSecurityCount}
              active={section === "privacy"}
              onClick={() => openTab("privacy")}
            />
            <NavRow
              icon={<IconBell size={18} />}
              color={TINT.coral}
              title="Notifications"
              subtitle={notificationsSummary}
              active={section === "notifications"}
              onClick={() => openTab("notifications")}
            />
            <NavRow
              icon={<IconChat size={18} />}
              color={TINT.blue}
              title="Chats"
              subtitle="Theme, text size, Enter key, disappearing messages"
              active={section === "chats"}
              onClick={() => openTab("chats")}
            />
          </Group>
          <Group>
            <NavRow
              icon={<IconGlobe size={18} />}
              color={TINT.teal}
              title="Network & delivery"
              subtitle={networkSummary}
              active={section === "network"}
              onClick={() => openTab("network")}
            />
            <NavRow
              icon={<IconData size={18} />}
              color={TINT.purple}
              title="Storage & data"
              subtitle="Space used, media cache, backup, delete everything"
              active={section === "storage"}
              onClick={() => openTab("storage")}
            />
            <NavRow
              icon={<IconInfo size={18} />}
              color={TINT.slate}
              title="Help & about"
              subtitle={APP_VERSION ? `Version ${APP_VERSION}` : "Privacy, terms, support"}
              active={section === "about"}
              onClick={() => openTab("about")}
            />
          </Group>

          {onLockNow && (
            <button
              type="button"
              className="btn secondary sv-lock-now"
              onClick={() => {
                close();
                onLockNow();
              }}
            >
              <IconLock size={18} />
              Lock now
            </button>
          )}
          <p className="sv-footer">Veto · Unscanned. Uncontrolled. End-to-end.</p>
        </nav>

        <div className="settings-body sv-page" ref={bodyRef}>
          <PageHead
            title={verifying ? verifying.displayName : pageTitle}
            backLabel={
              verifying ? "Verify contacts" : isSubPage ? "Privacy & security" : "Settings"
            }
            onBack={back}
            section={!isSubPage && !verifying}
            titleId={titleId}
          />
          {pages[current]}
        </div>
      </div>

      {chooser === "autolock" && (
        <ChoiceSheet<number>
          title="Lock Veto after"
          note={
            '"Right away" waits 15 seconds after you leave Veto, so picking a file doesn\'t lock you out. While Veto is open in front, it locks after at least a minute without activity.'
          }
          options={AUTO_LOCK_CHOICES.map((m) => [m, autoLockLabel(m)] as const)}
          value={
            AUTO_LOCK_CHOICES.includes(lockPrefs.autoLockMinutes) ? lockPrefs.autoLockMinutes : 5
          }
          onPick={(m) => updateLockPrefs({ ...lockPrefs, autoLockMinutes: m })}
          onClose={closeChooser}
        />
      )}
      {chooser === "disappear" && (
        <ChoiceSheet<DisappearTimerSec>
          title="New chats start with"
          note="Existing chats keep their own setting (chat menu → Disappearing messages)."
          options={DISAPPEAR_OPTIONS.map((o) => [o.sec, o.label] as const)}
          value={defaultDisappear}
          onPick={(sec) => {
            setDefaultDisappear(sec);
            saveDefaultDisappear(sec);
          }}
          onClose={closeChooser}
        />
      )}
      {changingPasscode && (
        <ChangePasscodeModal
          onClose={closePasscode}
          onDone={() => {
            setChangingPasscode(false);
            onToast("Passcode changed");
          }}
        />
      )}
    </ModalShell>
  );
}

function verifyStateLabel(c: Contact): string {
  return c.verificationState === "changed" ? "Keys changed — verify again" : "Not verified yet";
}

function eventLabel(kind: SecurityEvent["kind"]): string {
  switch (kind) {
    case "key_change":
      return "Key change";
    case "device_change":
      return "Device change";
    case "message_request":
      return "Message request";
    case "unverified_contact":
      return "Unverified contact";
    default:
      return kind;
  }
}

/** A contact line (avatar + name) for the Blocked and Verify pages. */
function PersonRow({
  contact,
  subtitle,
  warn,
  action,
  onClick,
}: {
  contact: Contact;
  subtitle?: string;
  warn?: boolean;
  action?: React.ReactNode;
  onClick?: () => void;
}) {
  const url = useProfileMediaUrl(contact.avatar ?? null);
  const inner = (
    <>
      <Avatar
        name={contact.displayName}
        size={40}
        media={url && contact.avatar ? { url, kind: contact.avatar.kind } : null}
      />
      <span className="sv-row-text">
        <span className="sv-row-title">{contact.displayName}</span>
        {subtitle && <span className={`sv-row-sub${warn ? " is-warn" : ""}`}>{subtitle}</span>}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className="sv-row sv-row-button sv-person" onClick={onClick}>
        {inner}
        <span className="sv-chevron" aria-hidden>
          ›
        </span>
      </button>
    );
  }
  return (
    <div className="sv-row sv-person">
      {inner}
      {action}
    </div>
  );
}

function LinkRow({
  href,
  icon,
  color,
  title,
  subtitle,
}: {
  href: string;
  icon: React.ReactNode;
  color: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <a
      className="sv-row sv-row-button sv-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <IconBadge icon={icon} color={color} />
      <span className="sv-row-text">
        <span className="sv-row-title">{title}</span>
        {subtitle && <span className="sv-row-sub">{subtitle}</span>}
      </span>
      <span className="sv-chevron" aria-hidden>
        ↗
      </span>
    </a>
  );
}
